import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpenText, FileText, Heart, PlayCircle, Sprout } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ScriptureVerse } from "@/components/patria/ScriptureVerse";
import FaithJourneyCard from "@/components/faith_journey/FaithJourneyCard";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

export default function VolunteerDashboard() {
  const { user } = useAuth();
  const [trainings, setTrainings] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [t, r] = await Promise.all([
          api.get("/training/mine"),
          api.get("/resources"),
        ]);
        setTrainings(t.data);
        setResources(r.data);
      } catch (err) {
        toast.error("We couldn't load your dashboard just now. Please try again.");
      } finally { setLoading(false); }
    })();
  }, []);

  const firstName = (user?.name || "friend").split(" ")[0];
  const dueTrainings = trainings.filter((t) => t.status !== "completed");

  return (
    <div className="space-y-8" data-testid="volunteer-dashboard">
      <section className="patria-card p-8 relative overflow-hidden">
        <div className="absolute top-4 right-4 flex items-center gap-2 text-xs uppercase tracking-widest text-terracotta-500 font-medium">
          <Sprout className="h-4 w-4" strokeWidth={1.5} /> Volunteer
        </div>
        <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
          Patria Church · Volunteers
        </p>
        <h1
          className="mt-2 text-4xl sm:text-5xl font-serif text-forest-900"
          data-testid="vol-dashboard-greeting"
        >
          {greeting()}, {firstName}.
        </h1>
        <p className="mt-3 text-charcoal-soft max-w-2xl">
          Thank you for showing up. This is your quiet, warm corner of MyPatria — trainings we've chosen for you, resources at your fingertips, and one clear path if you ever need to raise a concern.
        </p>
        <div className="mt-6">
          <ScriptureVerse />
        </div>
      </section>

      <FaithJourneyCard />

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="patria-card p-6 lg:col-span-2" data-testid="vol-trainings-card">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-forest-900/10 text-forest-900 flex items-center justify-center">
              <BookOpenText className="h-4 w-4" strokeWidth={1.5} />
            </div>
            <h2 className="font-serif text-2xl text-forest-900">My Trainings</h2>
          </div>
          <p className="mt-1 text-sm text-charcoal-soft">
            Take these at your own pace. Every step counts.
          </p>
          {loading && <p className="mt-4 italic text-charcoal-muted">Loading your trainings…</p>}
          {!loading && trainings.length === 0 && (
            <p className="mt-4 italic text-charcoal-muted">
              You have no trainings assigned yet — People Operations will let you know.
            </p>
          )}
          <div className="mt-4 space-y-3">
            {trainings.map((t) => (
              <Link
                key={t.id}
                to={`/training/${t.id}`}
                className="flex items-center justify-between gap-3 p-3 rounded-xl bg-cream-100 hover:bg-sage-200/60 transition"
                data-testid={`vol-training-${t.id}`}
              >
                <div className="flex items-center gap-3">
                  <PlayCircle className="h-5 w-5 text-forest-900" strokeWidth={1.5} />
                  <div>
                    <p className="font-medium text-forest-900">{t.training_title}</p>
                    <p className="text-xs text-charcoal-muted capitalize">
                      {t.status.replace("_", " ")} · Due {(t.due_date || "").slice(0, 10) || "—"}
                    </p>
                  </div>
                </div>
                <span className={t.status === "completed" ? "patria-chip-green" : "patria-chip-terra"}>
                  {t.status === "completed" ? "Done" : t.status === "in_progress" ? "In Progress" : "Start"}
                </span>
              </Link>
            ))}
          </div>
        </div>

        <div className="patria-card p-6 flex flex-col" data-testid="vol-er-card">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-terracotta-500/15 text-terracotta-600 flex items-center justify-center">
              <Heart className="h-4 w-4" strokeWidth={1.5} />
            </div>
            <h2 className="font-serif text-2xl text-forest-900">If You Ever Need To Speak Up</h2>
          </div>
          <p className="mt-2 text-sm text-charcoal-soft">
            The ER Center is a warm, confidential place to raise a concern — even anonymously. Nothing said there ever counts against you.
          </p>
          <a
            href="/er/anonymous"
            className="patria-btn-secondary mt-auto"
            data-testid="vol-er-link"
          >
            Open The Anonymous Intake
          </a>
        </div>
      </section>

      <section className="patria-card p-6" data-testid="vol-resources-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-full bg-forest-900/10 text-forest-900 flex items-center justify-center">
              <FileText className="h-4 w-4" strokeWidth={1.5} />
            </div>
            <h2 className="font-serif text-2xl text-forest-900">Resources For You</h2>
          </div>
          <Link to="/resources" className="text-sm text-forest-700 hover:text-forest-900 underline">
            Browse All Resources
          </Link>
        </div>
        {loading && <p className="mt-3 italic text-charcoal-muted">Loading…</p>}
        {!loading && resources.length === 0 && (
          <p className="mt-3 italic text-charcoal-muted">
            No resources yet — check back soon.
          </p>
        )}
        <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {resources.slice(0, 4).map((r) => (
            <li key={r.id} className="p-3 rounded-xl bg-cream-100" data-testid={`vol-res-${r.id}`}>
              <p className="text-[10px] uppercase tracking-widest text-charcoal-muted">{r.category}</p>
              <p className="font-medium text-forest-900 mt-1">{r.title}</p>
              <p className="text-xs text-charcoal-muted line-clamp-2">{r.description}</p>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
